// CB:BEGIN next-action-banner
/**
 * NextActionBanner
 *
 * Fetches /api/projects/:id/next-action and renders a contextual action banner.
 * Shows:
 *  - Headline + description
 *  - Who owns the next step
 *  - Primary + secondary CTA buttons
 *  - Blockers list (missing agreements etc.)
 *  - Help article links
 *  - Support / billing contacts
 *
 * Used by CustomerDashboard, ContractorActiveProjects, and admin ProjectCockpit.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { HelpPanel } from './HelpPanel';
import { ArrowRight, AlertCircle, Info, AlertTriangle } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

export type NextActionOwner = 'homeowner' | 'contractor' | 'admin' | 'system';
export type NextActionSeverity = 'info' | 'warning' | 'critical';

export interface NextAction {
  owner: NextActionOwner;
  code: string;
  headline: string;
  description: string;
  severity: NextActionSeverity;
  primaryCta?: { label: string; route: string };
  secondaryCta?: { label: string; route: string };
  blockers: string[];
  helpArticles: string[];
  contacts: { name: string; email: string; role: string }[];
}

interface Props {
  projectId: string;
  /** If provided, only render if next-action owner matches */
  forRole?: NextActionOwner;
  /** Compact mode — just a slim strip, no details */
  compact?: boolean;
  className?: string;
}

// ── Severity styles (Tailwind class sets) ─────────────────────────────────────

const SEVERITY_CLASSES: Record<NextActionSeverity, {
  wrapper: string;
  icon: React.ReactNode;
  badge: string;
  primaryBtn: string;
}> = {
  info: {
    wrapper: 'bg-blue-50 border border-blue-200 text-blue-900',
    icon: <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />,
    badge: 'bg-blue-100 text-blue-700',
    primaryBtn: 'bg-blue-700 hover:bg-blue-800 text-white',
  },
  warning: {
    wrapper: 'bg-amber-50 border border-amber-200 text-amber-900',
    icon: <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />,
    badge: 'bg-amber-100 text-amber-700',
    primaryBtn: 'bg-amber-700 hover:bg-amber-800 text-white',
  },
  critical: {
    wrapper: 'bg-red-50 border border-red-200 text-red-900',
    icon: <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />,
    badge: 'bg-red-100 text-red-700',
    primaryBtn: 'bg-red-700 hover:bg-red-800 text-white',
  },
};

const SEVERITY_LABEL: Record<NextActionSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Action Required',
};

const OWNER_LABELS: Record<NextActionOwner, string> = {
  homeowner: 'Homeowner',
  contractor: 'Contractor',
  admin: 'CoreBids Team',
  system: 'System',
};

// ── Component ─────────────────────────────────────────────────────────────────

export const NextActionBanner: React.FC<Props> = ({
  projectId,
  forRole,
  compact = false,
  className = '',
}) => {
  const { request } = useApi();
  const navigate = useNavigate();
  // Stable ref so `load` doesn't depend on `request` identity (prevents infinite re-render loop)
  const requestRef = useRef(request);
  useEffect(() => { requestRef.current = request; });

  const [action, setAction] = useState<NextAction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await requestRef.current<NextAction>(`/api/projects/${projectId}/next-action`);
      setAction(data);
    } catch (err: any) {
      // Soft-fail: don't crash the page if next-action fails
      console.warn('[NextActionBanner] Failed to load next action:', err?.message);
      setError('Could not load project status.');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // request intentionally omitted — stable via ref above

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return null; // Don't flash anything while loading
  if (error) return null;   // Soft-fail — don't show broken banner
  if (!action) return null;

  // Filter by role if provided
  if (forRole && action.owner !== forRole && action.owner !== 'system') return null;

  const sc = SEVERITY_CLASSES[action.severity];

  // ── Compact mode ─────────────────────────────────────────────────────────
  if (compact) {
    return (
      <div
        className={`next-action-banner next-action-banner--compact flex items-center gap-2.5 px-3.5 py-2 rounded-lg text-sm ${sc.wrapper} ${className}`}
      >
        {sc.icon}
        <span className="font-semibold flex-1">{action.headline}</span>
        {action.primaryCta && (
          <button
            onClick={() => navigate(action.primaryCta!.route)}
            className={`ml-auto inline-flex items-center gap-1 h-7 px-3 text-xs font-semibold rounded-md transition-colors ${sc.primaryBtn}`}
          >
            {action.primaryCta.label}
            <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  // ── Full mode ─────────────────────────────────────────────────────────────
  return (
    <div
      className={`next-action-banner rounded-xl p-5 mb-5 ${sc.wrapper} ${className}`}
      data-testid="next-action-banner"
      data-action-code={action.code}
      data-action-owner={action.owner}
    >
      {/* Header row */}
      <div className="flex items-start gap-3 mb-3">
        {sc.icon}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${sc.badge}`}>
              {SEVERITY_LABEL[action.severity]}
            </span>
            <span className="text-[11px] font-medium opacity-60">
              Action required by: {OWNER_LABELS[action.owner]}
            </span>
          </div>
          <div className="text-base font-bold leading-snug">{action.headline}</div>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm leading-relaxed mb-4">{action.description}</p>

      {/* Blockers */}
      {action.blockers.length > 0 && (
        <div className="bg-black/5 rounded-lg px-4 py-3 mb-4">
          <div className="text-xs font-semibold mb-2">Blocking items:</div>
          <ul className="list-disc list-inside text-sm space-y-1">
            {action.blockers.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </div>
      )}

      {/* CTAs */}
      <div className="flex flex-wrap gap-2.5 mb-4">
        {action.primaryCta && (
          <button
            onClick={() => navigate(action.primaryCta!.route)}
            data-testid="next-action-primary-cta"
            className={`inline-flex items-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-lg transition-colors ${sc.primaryBtn}`}
          >
            {action.primaryCta.label}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
        {action.secondaryCta && (
          <button
            onClick={() => navigate(action.secondaryCta!.route)}
            data-testid="next-action-secondary-cta"
            className="inline-flex items-center h-9 px-4 text-sm font-medium border border-current/30 rounded-lg hover:bg-black/5 transition-colors"
          >
            {action.secondaryCta.label}
          </button>
        )}
      </div>

      {/* Footer: help + contacts */}
      {(action.helpArticles.length > 0 || action.contacts.length > 0) && (
        <div className="flex flex-wrap gap-4 items-center border-t border-current/10 pt-3 text-xs opacity-75">
          {action.helpArticles.length > 0 && (
            <HelpPanel slugs={action.helpArticles} triggerLabel="Need help? View guide" />
          )}
          {action.contacts.map((c) => (
            <a
              key={c.email}
              href={`mailto:${c.email}`}
              className="hover:underline"
            >
              {c.name} ({c.email})
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default NextActionBanner;
// CB:END next-action-banner
